import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { SearchField } from '@sunwebgroup/uds-ui/fields/search-field'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { IndeterminateProgressCircle } from '@sunwebgroup/uds-ui/IndeterminateProgressCircle'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import {
  type RoomParticipantFilterType,
  RoomParticipantFilterValues,
} from '../../domain/types'
import { useGroupResponsibleStore } from '../../infrastructure/useGroupResponsibleStore'
import {
  getFilteredRoomParticipants,
  getRoomExtraInfo,
  getRoomParticipantCounts,
  isMaxOccupancyReached,
  isOverCapacity,
  isRoomChanged,
} from '../selectors'
import { getEditDeadLine } from '../selectors/commonSelectors'
import { EditRoomParticipantItem } from './EditRoomParticipantItem'
import styles from './EditRoomSheetPortal.module.css'
import { FilterToggleGroup } from './FilterToggleGroup'

const ROOM_PARTICIPANT_FILTERS = [
  { value: RoomParticipantFilterValues.ALL, label: 'groups_responsible_all', countKey: 'all' as const },
  { value: RoomParticipantFilterValues.UNASSIGNED, label: 'groups_responsible_room_unassigned', countKey: 'unassigned' as const },
  { value: RoomParticipantFilterValues.ASSIGNED, label: 'groups_responsible_room_assigned', countKey: 'assigned' as const },
]

export function EditRoomSheetPortal() {
  const intl = useIntl()
  const locale = useConfiguration('locale')
  const {
    deadLineDate,
    daysBefore,
    // isOverDeadline,
  } = useGroupResponsibleStore(state => getEditDeadLine(state, locale, 'room'))
  const [searchTerm, setSearchTerm] = React.useState('')
  const [filter, setFilter] = React.useState<RoomParticipantFilterType>(RoomParticipantFilterValues.ALL)
  const isEditingRoomAssignment = useGroupResponsibleStore(state => state.isEditingRoomAssignment)
  const toggleRoomParticipantSelection = useGroupResponsibleStore(state => state.toggleRoomParticipantSelection)
  const selectedRoomParticipantIds = useGroupResponsibleStore(state => state.selectedRoomParticipantIds)
  const closeRoomAssignment = useGroupResponsibleStore(state => state.closeRoomAssignment)
  const roomExtraInfo = useGroupResponsibleStore(getRoomExtraInfo)
  const maxOccupancyReached = useGroupResponsibleStore(isMaxOccupancyReached)
  const roomAssignmentStatus = useGroupResponsibleStore(state => state.roomAssignmentStatus)
  const isSubmitting = roomAssignmentStatus === 'submitting'
  const hasError = roomAssignmentStatus === 'error'
  const hasOverCapacity = useGroupResponsibleStore(isOverCapacity)
  const isOverDeadline = false // Temporary disable deadline check

  const stageCurrentRoomAssignment = useGroupResponsibleStore(state => state.stageCurrentRoomAssignment)
  const activeRoomId = useGroupResponsibleStore(state => state.activeExtraInfoId)
  const isChanged = useGroupResponsibleStore(state => isRoomChanged(state, activeRoomId))

  const filteredParticipants = useGroupResponsibleStore(state =>
    getFilteredRoomParticipants(state, filter, searchTerm),
  )

  const counts = useGroupResponsibleStore(getRoomParticipantCounts)

  React.useEffect(() => {
    if (isEditingRoomAssignment) {
      setSearchTerm('')
      setFilter(RoomParticipantFilterValues.ALL)
    }
  }, [isEditingRoomAssignment])

  function handleOpenChange(open: boolean) {
    if (!open && isSubmitting) {
      return
    }
    if (!open) {
      closeRoomAssignment()
    }
  }

  function handleAddToChanges() {
    if (hasOverCapacity) {
      return
    }
    stageCurrentRoomAssignment()
  }

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value)
  }

  return (
    <Sheet.Root open={isEditingRoomAssignment} onOpenChange={handleOpenChange}>
      <Sheet.Body side="right">
        <Sheet.Header
          className={cn(isSubmitting && styles.disabled)}
          closeLabel={intl.formatMessage({ id: 'close' })}
          titleLabel={intl.formatMessage({ id: 'common_edit' })}
        />
        <Sheet.Content className={cn(isSubmitting && styles.disabled)}>
          {isOverDeadline
            ? (
                <>
                  <Typography variant="display-g-regular" as="div">
                    {intl.formatMessage({ id: 'groups_responsible_room_assignment_title' }, { roomName: roomExtraInfo.name })}
                  </Typography>
                  <Banner className={styles.deadlineBanner} color="warning" size="M" hasIcon>
                    <Banner.TextsWrapper>
                      <Banner.Text>
                        {intl.formatMessage({ id: 'groups_responsible_room_assignment_is_over_deadline_title' })}
                      </Banner.Text>
                      <Banner.SupportingText>
                        {intl.formatMessage(
                          { id: 'groups_responsible_room_assignment_is_over_deadline_description' },
                          { deadLineDate, daysBefore },
                        )}
                      </Banner.SupportingText>
                    </Banner.TextsWrapper>
                  </Banner>
                  <Button fullWidth onClick={() => handleOpenChange(false)}>
                    {intl.formatMessage({ id: 'close' })}
                  </Button>
                </>
              )
            : (
                <>
                  <div className={styles.stickyHeader}>
                    <div className={styles.titleWrapper}>
                      <Typography variant="display-g-regular" as="div">
                        {intl.formatMessage({ id: 'groups_responsible_room_assignment_title' }, { roomName: roomExtraInfo.name })}
                      </Typography>
                      {isChanged && (
                        <Tag variant="informative-tinted" size="M">
                          {intl.formatMessage({ id: 'groups_responsible_room_changed_label' })}
                        </Tag>
                      )}
                    </div>
                    <div className={styles.descriptionTextContainer}>
                      <Typography variant="body-m-regular-snug" as="div">
                        {intl.formatMessage({ id: 'groups_responsible_room_assignment_description' }, {
                          selectedParticipants: selectedRoomParticipantIds.size,
                          maxOccupancy: roomExtraInfo.maxOccupancy,
                        })}
                      </Typography>
                      <Typography variant="body-m-regular-snug" as="div">
                        {intl.formatMessage({ id: 'groups_responsible_room_assignment_description_deadline' }, {
                          deadLineDate,
                          daysBefore,
                        })}
                      </Typography>
                    </div>
                    {maxOccupancyReached && (
                      <Banner className={styles.stagingBanner} color="informative" size="M" hasIcon contentIsCentered>
                        <Banner.TextsWrapper>
                          <Banner.Text>{intl.formatMessage({ id: 'groups_responsible_room_assignment_max_occupancy_reached' })}</Banner.Text>
                        </Banner.TextsWrapper>
                      </Banner>
                    )}
                    {hasOverCapacity && (
                      <Banner className={styles.stagingBanner} color="danger" size="M" hasIcon contentIsCentered>
                        <Banner.TextsWrapper>
                          <Banner.Text>{intl.formatMessage({ id: 'groups_responsible_room_assignment_over_capacity_error' })}</Banner.Text>
                        </Banner.TextsWrapper>
                      </Banner>
                    )}
                    {hasError && (
                      <Banner className={styles.stagingBanner} color="danger" size="M" hasIcon contentIsCentered>
                        <Banner.TextsWrapper>
                          <Banner.Text>{intl.formatMessage({ id: 'groups_responsible_room_assignment_error' })}</Banner.Text>
                        </Banner.TextsWrapper>
                      </Banner>
                    )}
                    <SearchField
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder={intl.formatMessage({ id: 'live-search_search_button_text' })}
                      clearButtonAriaLabel={intl.formatMessage({ id: 'clear_search_button_aria_label' })}
                    />
                    <FilterToggleGroup
                      filter={filter}
                      filters={ROOM_PARTICIPANT_FILTERS}
                      counts={counts}
                      onFilterChange={setFilter}
                      size="S"
                    />
                  </div>
                  {filteredParticipants.length === 0
                    ? (
                        <div className={styles.emptyMessage}>
                          <Icon glyph="FaceSad" size={36} />
                          <Typography variant="body-m-bold-snug" as="div">
                            {intl.formatMessage({ id: 'groups_responsible__no_data_title' })}
                          </Typography>
                        </div>
                      )
                    : (
                        <div className={styles.participantList}>
                          {filteredParticipants.map((participant) => {
                            const isSelected = selectedRoomParticipantIds.has(participant.bookingCustomerId.toString())
                            const isDisabled = !isSelected && (maxOccupancyReached || hasOverCapacity)

                            return (
                              <EditRoomParticipantItem
                                key={participant.id}
                                id={participant.id}
                                name={participant.name}
                                bookingCustomerId={participant.bookingCustomerId}
                                isSelected={isSelected}
                                isDisabled={isDisabled}
                                onToggle={id => toggleRoomParticipantSelection(id.toString())}
                              />
                            )
                          })}
                        </div>
                      )}
                </>
              )}
        </Sheet.Content>
        {isSubmitting && (
          <div className={styles.progressCircle}>
            <IndeterminateProgressCircle />
          </div>
        )}
        {!isOverDeadline && (
          <Sheet.Footer>
            <Button onClick={handleAddToChanges} disabled={hasOverCapacity}>
              {intl.formatMessage({ id: 'groups_responsible_room_assignment_add_to_changes' })}
            </Button>
          </Sheet.Footer>
        )}
      </Sheet.Body>
    </Sheet.Root>
  )
}
